import type {
  AccessorialRateInput,
  AccessorialRateListQuery,
  AccessorialUnit,
  CreateRateCardInput,
  EndAccessorialRateInput,
  MoneyJson,
  OffcardReportGroup,
  OffcardReportQuery,
  Page,
  RateBasis,
  RateCardListQuery,
  RateLineInput,
  RateLineListQuery,
  RateLineProposalEditInput,
  RateLineProposalKind,
  RateLineProposalListQuery,
  RateLineProposalStatus,
  SupersedeAccessorialRateInput,
  SupersedeRateLineInput,
} from '@chukua/common';
import { api } from '../../shared/shared.api';
import { toListParams } from '../../shared/policies/list-params';

export interface RateCardView {
  id: string;
  customerId: string | null;
  /** Null on the standard card, which has no customer. */
  customerName: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RateLineView {
  id: string;
  rateCardId: string;
  originZoneCode: string;
  destinationZoneCode: string;
  jobTypeCode: string;
  containerSizeCode: string | null;
  containerTypeCode: string | null;
  weightBandCode: string | null;
  basis: RateBasis;
  amount: MoneyJson;
  emptyReturnIncluded: boolean | null;
  validFrom: string;
  validTo: string | null;
  supersedesLineId: string | null;
  supersededByLineId: string | null;
  createdAt: string;
}

export interface RateLineProposalView {
  id: string;
  rateCardId: string;
  kind: RateLineProposalKind;
  status: RateLineProposalStatus;
  originZoneCode: string;
  destinationZoneCode: string;
  jobTypeCode: string;
  containerSizeCode: string | null;
  containerTypeCode: string | null;
  weightBandCode: string | null;
  basis: RateBasis;
  amount: MoneyJson;
  emptyReturnIncluded: boolean | null;
  validFrom: string;
  validTo: string | null;
  supersedesLineId: string | null;
  rateLineId: string | null;
  authorId: string;
  submittedAt: string | null;
  decidedById: string | null;
  decidedAt: string | null;
  lastDecisionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A pending proposal with where it belongs and the line it would replace, so the approver needs no second read. */
export interface RateLineProposalQueueView extends RateLineProposalView {
  rateCardCustomerId: string | null;
  rateCardCustomerName: string | null;
  previousLine: RateLineView | null;
}

export interface AccessorialRateView {
  id: string;
  rateCardId: string;
  accessorialTypeCode: string;
  amount: MoneyJson;
  freeQuantity: number | null;
  validFrom: string;
  validTo: string | null;
  supersedesRateId: string | null;
  createdAt: string;
}

export interface AccessorialType {
  id: string;
  code: string;
  name: string;
  unit: AccessorialUnit;
  hasFreeQuantity: boolean;
  isActive: boolean;
}

export type RateLineImportRowStatus = 'new' | 'changed' | 'unchanged' | 'invalid' | 'historical';
export type RateLineImportRowOutcome = 'created' | 'skipped' | 'error';

export interface RateLineImportFieldError {
  /** Unset for a commit-time failure with no cell to blame. */
  field?: string;
  message: string;
}

/** One sheet row classified against the card: `value`'s fields are present whenever the row's shape and codes parsed, whatever its status. */
export interface RateLineImportRowView {
  rowNumber: number;
  status: RateLineImportRowStatus;
  originZoneCode?: string;
  destinationZoneCode?: string;
  jobTypeCode?: string;
  containerSizeCode?: string | null;
  containerTypeCode?: string | null;
  weightBandCode?: string | null;
  basis?: RateBasis;
  amount?: MoneyJson;
  emptyReturnIncluded?: boolean;
  validFrom?: string;
  validTo?: string | null;
  currentLine?: RateLineView;
  errors?: RateLineImportFieldError[];
}

export type RateLineImportPreviewResult = { ok: false; missingHeaders: string[] } | { ok: true; rows: RateLineImportRowView[] };

export interface RateLineImportCommitRow {
  rowNumber: number;
  status: RateLineImportRowStatus;
  outcome: RateLineImportRowOutcome;
  errors?: RateLineImportFieldError[];
}

export type RateLineImportCommitResult = { ok: false; missingHeaders: string[] } | { ok: true; rows: RateLineImportCommitRow[]; createdCount: number };

const post = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body) });

export function listRateCards(query: RateCardListQuery): Promise<Page<RateCardView>> {
  return api<Page<RateCardView>>(`/rate-cards?${toListParams(query)}`);
}

export function getRateCard(id: string): Promise<RateCardView> {
  return api<RateCardView>(`/rate-cards/${id}`);
}

export function createRateCard(input: CreateRateCardInput): Promise<void> {
  return api<void>('/rate-cards', post(input));
}

export async function deactivateRateCard(id: string): Promise<void> {
  await api<void>(`/rate-cards/${id}/deactivate`, { method: 'POST' });
}

export function listRateLines(rateCardId: string, query: RateLineListQuery): Promise<Page<RateLineView>> {
  return api<Page<RateLineView>>(`/rate-cards/${rateCardId}/lines?${toListParams(query)}`);
}

/** Opens a draft proposal, not a line: nothing is priced from it until it is submitted and approved. */
export function createRateLine(rateCardId: string, input: RateLineInput): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-cards/${rateCardId}/lines`, post(input));
}

export function supersedeRateLine(lineId: string, input: SupersedeRateLineInput): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-lines/${lineId}/supersede`, post(input));
}

export function listCardProposals(rateCardId: string, query: RateLineProposalListQuery): Promise<Page<RateLineProposalView>> {
  return api<Page<RateLineProposalView>>(`/rate-cards/${rateCardId}/proposals?${toListParams(query)}`);
}

export function listApprovalQueue(query: RateLineProposalListQuery): Promise<Page<RateLineProposalQueueView>> {
  return api<Page<RateLineProposalQueueView>>(`/rate-line-proposals?${toListParams(query)}`);
}

export function editProposal(id: string, input: RateLineProposalEditInput): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-line-proposals/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
}

export function submitProposal(id: string, reason: string): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-line-proposals/${id}/submit`, post({ reason }));
}

export function withdrawProposal(id: string, reason: string): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-line-proposals/${id}/withdraw`, post({ reason }));
}

export function approveProposal(id: string, reason: string): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-line-proposals/${id}/approve`, post({ reason }));
}

export function rejectProposal(id: string, reason: string): Promise<RateLineProposalView> {
  return api<RateLineProposalView>(`/rate-line-proposals/${id}/reject`, post({ reason }));
}

export function listAccessorialRates(rateCardId: string, query: AccessorialRateListQuery): Promise<Page<AccessorialRateView>> {
  return api<Page<AccessorialRateView>>(`/rate-cards/${rateCardId}/accessorial-rates?${toListParams(query)}`);
}

export function createAccessorialRate(rateCardId: string, input: AccessorialRateInput): Promise<AccessorialRateView> {
  return api<AccessorialRateView>(`/rate-cards/${rateCardId}/accessorial-rates`, post(input));
}

export function supersedeAccessorialRate(id: string, input: SupersedeAccessorialRateInput): Promise<AccessorialRateView> {
  return api<AccessorialRateView>(`/accessorial-rates/${id}/supersede`, post(input));
}

export function endAccessorialRate(id: string, input: EndAccessorialRateInput): Promise<AccessorialRateView> {
  return api<AccessorialRateView>(`/accessorial-rates/${id}/end`, post(input));
}

/** The whole catalogue, active and inactive: a small fixed list (CLAUDE.md → Lists and tables). */
export function listAccessorialTypes(): Promise<AccessorialType[]> {
  return api<AccessorialType[]>('/accessorial-types');
}

/** Jobs priced on and off the card, by customer, lane and month (gitaumoses4/chukua#35). */
export function listOffcardReport(query: OffcardReportQuery): Promise<Page<OffcardReportGroup>> {
  return api<Page<OffcardReportGroup>>(`/offcard-pricing-report?${toListParams(query)}`);
}

/** A navigation, not an `api()` JSON call: the browser downloads straight from the (cookie-authenticated, same-origin) API. */
export function rateLineTemplateUrl(): string {
  return '/api/v1/rate-lines/template';
}

export function rateLineExportUrl(rateCardId: string, includeSuperseded: boolean): string {
  return `/api/v1/rate-cards/${rateCardId}/lines/export?includeSuperseded=${includeSuperseded}`;
}

function importFormData(file: File, fields: Record<string, string> = {}): FormData {
  const body = new FormData();
  body.append('file', file);
  for (const [key, value] of Object.entries(fields)) body.append(key, value);
  return body;
}

/** Classifies the sheet against the card without writing anything — new / changed / unchanged / invalid, per row. */
export function previewRateLineImport(rateCardId: string, file: File): Promise<RateLineImportPreviewResult> {
  return api<RateLineImportPreviewResult>(`/rate-cards/${rateCardId}/lines/import/preview`, { method: 'POST', body: importFormData(file) });
}

/** Opens a draft proposal per new/changed row, exactly as a hand-typed price change would — never a published line. */
export function commitRateLineImport(rateCardId: string, file: File, reason: string): Promise<RateLineImportCommitResult> {
  return api<RateLineImportCommitResult>(`/rate-cards/${rateCardId}/lines/import/commit`, { method: 'POST', body: importFormData(file, { reason }) });
}
